import {MedicationProfileFormSchema} from '@/modules/drug-catalog/medication-profiles/schemas'
import {
  getStabilityRuleFormDefaultValues,
  mapStabilityRuleFormToApiValues,
} from '@/modules/drug-catalog/stability-rules/functions'
import {StabilityRuleFormSchema} from '@/modules/drug-catalog/stability-rules/schemas'
import {elementToArray, getFirstElementOrSelf} from '@cypro/ui/utils'
import {AddMedicationProfileSchema} from '@cypro/engine/mutators'
import {MedicationProfile, StabilityRule} from '@cypro/engine/schema'

export function getMedicationProfileFormDefaultValues(
  profile: MedicationProfile | undefined
): MedicationProfileFormSchema {
  return {
    id: profile?.id,
    medicineFormId: elementToArray(profile?.medicineFormId),
    medicineFormName: profile?.medicineForm?.name || '',
    name: profile?.name || '',
    oralQuantityBasis: elementToArray(profile?.oralQuantityBasis),
    oralSpecificsRequired: Boolean(profile?.oralQuantityBasis),
    reconstitutionDiluentsIds:
      profile?.allowedReconstitutionDiluents?.map(profileDiluent => profileDiluent.solutionDrugId) || [],
    reconstitutionRequired: Boolean(profile?.reconstitutionRequired),
    isDrySubstance: Boolean(profile?.isDrySubstance),
    stabilityRules: profile?.stabilityRules?.map(item => getStabilityRuleFormDefaultValues(item.stabilityRule)) || [],
    status: elementToArray(profile?.status || 'active'),
  }
}

export function mapMedicationProfileFormToApiValues(profile: MedicationProfileFormSchema): AddMedicationProfileSchema {
  const stabilityRules = profile.stabilityRules.map(mapStabilityRuleFormToApiValues)

  return {
    ...profile,
    medicineFormId: getFirstElementOrSelf(profile.medicineFormId),
    oralQuantityBasis: profile.oralSpecificsRequired ? getFirstElementOrSelf(profile.oralQuantityBasis) : undefined,
    status: getFirstElementOrSelf(profile.status),
    stabilityRules,
  }
}

export function mapMedicationProfilesToSelectItems(
  profiles: MedicationProfile[] | readonly MedicationProfile[] | undefined
) {
  if (!profiles) return []
  return profiles.map(profile => ({label: profile.name || profile?.medicineForm?.name || '', value: profile.id}))
}

export function isMedicationProfileResource(
  input: MedicationProfile | MedicationProfileFormSchema
): input is MedicationProfile {
  if (!input) return false
  return 'createdAt' in input || 'createdBy' in input // exists only on MedicationProfile
}

export function isMedicationProfileForm(
  input: MedicationProfile | MedicationProfileFormSchema
): input is MedicationProfileFormSchema {
  if (!input) return false
  return !isMedicationProfileResource(input)
}

export function getMedicationProfileResourceStabilityRules(profile: MedicationProfile | undefined) {
  if (!profile) return []
  return (profile.stabilityRules?.map(item => item.stabilityRule)?.filter(Boolean) as StabilityRule[]) || []
}

export function getMedicationProfileStabilityRules(
  profile: MedicationProfile | MedicationProfileFormSchema | undefined
): (StabilityRule | StabilityRuleFormSchema)[] {
  if (!profile) return []
  if (isMedicationProfileResource(profile)) getMedicationProfileResourceStabilityRules(profile)
  if (isMedicationProfileForm(profile)) return profile.stabilityRules || []
  return []
}
